/**
 * Shared state for stock planning sub-tabs: page size, search input with
 * 300ms debounce, current page, and helpers to reset on filter change.
 *
 * @param initialPartNumber - When the parent grid has a part selected,
 *   pass it in so the search input pre-fills. Changes to this value
 *   re-seed the search box.
 */
import { useEffect, useState } from 'react';

export interface SubTabControls {
  pageSize: number;
  setPageSize: (n: number) => void;
  searchInput: string;
  setSearchInput: (s: string) => void;
  /** Debounced search value — use this in the API request params. */
  search: string;
  page: number;
  setPage: (n: number) => void;
}

export function useSubTabControls(initialPartNumber?: string): SubTabControls {
  const [pageSize, _setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [searchInput, _setSearchInput] = useState(initialPartNumber ?? '');
  const [search, setSearch] = useState(initialPartNumber ?? '');

  // Debounce searchInput -> search by 300ms.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // When the parent's selected part changes, re-seed the search box.
  useEffect(() => {
    _setSearchInput(initialPartNumber ?? '');
    setSearch(initialPartNumber ?? '');
    setPage(1);
  }, [initialPartNumber]);

  const setSearchInput = (s: string) => {
    _setSearchInput(s);
    setPage(1);
  };

  const setPageSize = (n: number) => {
    _setPageSize(n);
    setPage(1);
  };

  return {
    pageSize,
    setPageSize,
    searchInput,
    setSearchInput,
    search,
    page,
    setPage,
  };
}
